import { AddAiCreationArgs, ClassroomPortfolioService } from './ClassroomPortfolioService';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { extractVideoPoster } from './aiCreation';
import { db } from '../../../shared/storage/firebase';

export type AiUploadTaskStatus = 'queued' | 'uploading' | 'complete' | 'failed';

export interface AiUploadTask {
  id: string;
  entryId: string;
  label: string;
  status: AiUploadTaskStatus;
  progress: number;
  error?: string;
  canRetry?: boolean;
}

type Listener = (tasks: AiUploadTask[]) => void;

export class AiUploadQueue {
  private tasks: AiUploadTask[] = [];
  private listeners = new Set<Listener>();
  private retryArgs = new Map<string, { args: AddAiCreationArgs; label: string }>();
  private entryChains = new Map<string, Promise<void>>();

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener([...this.tasks]);
    return () => this.listeners.delete(listener);
  }

  enqueue(args: AddAiCreationArgs, label: string): string {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    args = { ...args, creationId: args.creationId || id };
    this.retryArgs.set(id, { args, label });
    this.tasks = [...this.tasks, { id, entryId: args.entryId, label, status: 'queued', progress: 0 }];
    this.emit();

    const previous = this.entryChains.get(args.entryId) || Promise.resolve();
    const run = previous.catch(() => undefined).then(async () => {
      this.patch(id, { status: 'uploading', progress: args.file ? 1 : 20 });
      try {
        if (args.file && args.type === 'video' && !args.posterFile && !args.posterUrl) {
          try { args.posterFile = await extractVideoPoster(args.file); } catch { /* Filename fallback is supported. */ }
        }
        const creation = await ClassroomPortfolioService.addAiCreation({
          ...args,
          onProgress: (progress) => this.patch(id, { progress }),
        });
        if (args.nominateForLibrary && args.libraryContentType) {
          const displayAssetUrl = ['video', 'audio'].includes(creation.type || '') ? creation.url || '' : creation.posterUrl || creation.url || '';
          try { await addDoc(collection(db, 'libraryItems'), {
            status: 'nominated',
            contentType: args.libraryContentType,
            title: String(creation.caption || creation.filename || 'Untitled creation').trim(),
            description: String(creation.caption || '').trim(),
            displayAssetUrl,
            originalAssetUrl: creation.url || '',
            visibility: 'community', gameEligible: ['character', 'world'].includes(args.libraryContentType), featured: true, defaultsVersion: 2,
            sourceType: 'student_creation', sourcePortfolioCreationId: creation.id,
            sourcePortfolioEntryId: args.entryId,
            ...(args.sourceLesson ? { sourceLesson: args.sourceLesson } : {}),
            previewAssetUrl: creation.posterUrl || '',
            squadId: args.squadId, squadName: args.squadName || '',
            teamId: args.teamId, teamName: args.teamName || '',
            attribution: args.teamName ? 'team_and_squad' : 'overture_students',
            ...(args.libraryContentType === 'character' ? { character: {} } : {}),
            ...(args.libraryContentType === 'world' ? { world: {} } : {}),
            ...(args.libraryContentType === 'movie' ? { movie: {} } : {}),
            ...(args.libraryContentType === 'model_3d' ? { model3d: { mediaType: creation.type === 'video' ? 'video' : 'image' } } : {}),
            ...(args.libraryContentType === 'game' ? { game: { url: creation.url || '' } } : {}),
            submittedBy: args.uploadedBy, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
          }); } catch (nominationError) {
            console.error('AI creation uploaded, but Library nomination failed', nominationError);
            this.patch(id, { status: 'failed', progress: 100, error: 'Upload complete, but the Library nomination failed. Nominate it from the portfolio.' });
            return;
          }
        }
        this.retryArgs.delete(id);
        this.patch(id, { status: 'complete', progress: 100 });
        window.setTimeout(() => this.dismiss(id), 6000);
      } catch (error: any) {
        this.patch(id, {
          status: 'failed',
          error: error?.message || 'Upload failed. Retry when your connection is ready.',
          canRetry: true,
        });
      }
    });
    const chained = run.finally(() => {
      if (this.entryChains.get(args.entryId) === chained) this.entryChains.delete(args.entryId);
    });
    this.entryChains.set(args.entryId, chained);
    return id;
  }

  retry(id: string): void {
    const saved = this.retryArgs.get(id);
    if (!saved || !this.tasks.some(task => task.id === id && task.status === 'failed' && task.canRetry)) return;
    this.dismiss(id);
    this.enqueue(saved.args, saved.label);
  }

  dismiss(id: string): void {
    this.retryArgs.delete(id);
    this.tasks = this.tasks.filter((task) => task.id !== id);
    this.emit();
  }

  private patch(id: string, update: Partial<AiUploadTask>): void {
    this.tasks = this.tasks.map((task) => task.id === id ? { ...task, ...update } : task);
    this.emit();
  }

  private emit(): void {
    const snapshot = [...this.tasks];
    this.listeners.forEach((listener) => listener(snapshot));
  }
}

export const aiUploadQueue = new AiUploadQueue();
